import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import NumberFormat from "react-number-format";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import {
   Alert as ReactstrapAlert,
   Button, ButtonGroup, Card, CardBody, Col,
   FormFeedback, Input, Label, Modal,
   ModalBody, ModalFooter, ModalHeader, Row
} from "reactstrap";
import { CartProductCard, ConfirmModal, OrderDetails, ServiceThreadSpinner } from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, UserContext } from "../utils";

const RESELECT_CUSTOMER_MESSAGE = "Please navigate back to Customer Dashboard and reselect a customer...";
// Order/PlaceOrder placed the orders but could not reload the cart, so the cart on screen is stale.
const CART_REFRESH_FAILED_MESSAGE = "Your cart could not be refreshed. Please reload the page before placing anything else.";
const PO_CHECK_FAILED_MESSAGE = "The duplicate PO check could not be completed. Please try again.";

// api.js's global response interceptor RETURNS a failed request's error object instead of rejecting, so a failed
// duplicate-PO check can arrive as a FULFILLED value. Only a real reply - a data object carrying a boolean
// success - counts as an answer; anything else is a failed check.
function isWellFormedReply(value) {
   return _.isObjectLike(value?.data) && _.isBoolean(value.data.success);
}

// ONE toast for the whole placement, one line per order in the server's order, showing each server sentence
// as is. App.js's ToastContainer shows one toast at a time (limit={1}), so a toast per order would queue the
// later ones out of sight. Styled by the most serious outcome present: anything not placed (refused, not
// placed, unconfirmed - or an outcome this client does not know) is an error that stays up until dismissed,
// because the user must act or must NOT act (an unconfirmed order must not be placed again); otherwise a
// warning if any confirmation failed; otherwise success. extraLines (the cart-refresh note) and a group whose
// cart lines could not be removed (cartLinesNotRemoved - the user must remove them) always need action.
function showPlacementResults(results, extraLines) {
   const lines = _.concat(_.map(results, "message"), extraLines);
   if (!lines.length) return;
   const outcomes = _.map(results, "outcome");
   const placedOutcomes = [constants.PLACE_ORDER_OUTCOME.PLACED, constants.PLACE_ORDER_OUTCOME.PLACED_WITH_WARNING];
   const needsAction = extraLines.length > 0
      || _.some(outcomes, (outcome) => !_.includes(placedOutcomes, outcome))
      || _.some(results, "cartLinesNotRemoved");
   const text = _.join(lines, "\n");   // the container's toast-multiline class is white-space: pre-line
   if (needsAction) {
      toast.error(text, { autoClose: false });
   } else if (_.includes(outcomes, constants.PLACE_ORDER_OUTCOME.PLACED_WITH_WARNING)) {
      toast.warning(text);
   } else {
      toast.success(text);
   }
}

// locked: true while OrderPlacement's submit is in flight - the list is part of the order already on its way.
const OrderEmailList = ({ emailList, setEmailList, quickEmails, userCtx, locked }) => {
   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [emailInvalid, setEmailInvalid] = useState(null);
   const [emailToDelete, setEmailToDelete] = useState(null);
   const [newEmail, setNewEmail] = useState("");
   let modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";

   function allowEdit(index) {
      setEditCopy(emailList[index]);
      setEditIndex(index);
   }

   function cancelEdit() {
      setEditCopy(null);
      setEditIndex(null);
   }

   function deleteEmail() {
      const emailCopy = _.filter(emailList, (email) => email !== emailToDelete);
      setEmailList(emailCopy);
      setEmailToDelete(null);
   }

   function tryAddEmail(email) {
      if (helpers.emailIsValid(email)) {
         setEmailInvalid(false);
         const emailListCopy = _.slice(emailList);

         if (
            _.findIndex(emailListCopy, (currentEmail) => {
               return currentEmail === email;
            }) !== -1
         ) {
            return;
         }

         if (editIndex === null) {
            emailListCopy.push(email);
            setNewEmail("");
         } else {
            emailListCopy[editIndex] = email;
            setEditIndex(null);
         }
         setEmailList(emailListCopy);
      } else {
         setEmailInvalid(true);
      }
   }

   return (
      <>
         <Row className="mt-2">
            <Label children="CC Emails:" xs={2} className="fw-bold" />
            <Col xs={8}>
               <Input
                  id="newEmail"
                  className="ml4"
                  value={newEmail || ""}
                  onChange={(e) => {
                     setNewEmail(e.target.value);
                     if (!e.target.value) setEmailInvalid(false);
                  }}
                  invalid={emailInvalid && newEmail}
                  disabled={locked}
               />
               {emailInvalid && newEmail && (
                  <FormFeedback children="Invalid email. Please correct and try again." />
               )}
            </Col>
            <Col xs={2}>
               <Button
                  className="shredButton w-100"
                  onClick={() => tryAddEmail(newEmail)}
                  children="Add Email"
                  disabled={locked}
               />
            </Col>
         </Row>
         {_.map(emailList, (email, index) => {
            return (
               <Row key={index} className="mt-2">
                  <Col xs={{ size: 8 }} lg={{ size: 8, offset: 2 }}>
                     <Input
                        value={editIndex === index ? editCopy : email}
                        onChange={(e) => setEditCopy(e.target.value)}
                        invalid={emailInvalid && editIndex === index}
                        className={`${editIndex === index ? "" : "readonly-input"}`}
                        readOnly={editIndex !== index}
                        disabled={locked}
                     />
                     {emailInvalid && editIndex === index && (
                        <FormFeedback
                           visible={emailInvalid}
                           children="Invalid email. Please correct and try again."
                        />
                     )}
                  </Col>
                  <Col className="text-center" xs={4} lg={2}>
                     <ButtonGroup className="float-end">
                        <Button
                           className="icon-button me-2"
                           color={editIndex === index ? "success" : "info"}
                           onClick={
                              editIndex === index
                                 ? () => tryAddEmail(editCopy)
                                 : () => allowEdit(index)
                           }
                           disabled={locked || (editIndex !== null && editIndex !== index)}
                        >
                           <FontAwesomeIcon
                              className="whiteIcon"
                              icon={editIndex === index ? "check" : "edit"}
                           />
                        </Button>
                        <Button
                           className="icon-button"
                           color={editIndex === index ? "secondary" : "danger"}
                           onClick={
                              editIndex === index
                                 ? () => cancelEdit()
                                 : () => setEmailToDelete(email)
                           }
                           title={
                              editIndex === index ? "Discard Changes" : "Delete Email"
                           }
                           disabled={locked || (editIndex !== null && editIndex !== index)}
                        >
                           <FontAwesomeIcon
                              className="whiteIcon"
                              icon={editIndex === index ? "times" : "trash"}
                           />
                        </Button>
                     </ButtonGroup>
                  </Col>
               </Row>
            );
         })}
         <hr className="my-2" />
         <Row>
            <Label children="Quick Add Emails:" xs={2} className="fw-bold" />
            <Col xs={10}>
               {_.map(quickEmails, (email, index) => {
                  return (
                     <Row key={index} className="mt-1">
                        <Col>{email}</Col>
                        <Col>
                           <Button
                              className="shredButton float-end"
                              onClick={() => tryAddEmail(email)}
                              disabled={
                                 locked ||
                                 _.findIndex(emailList, (currentEmail) => {
                                    return currentEmail === email;
                                 }) !== -1
                              }
                           >
                              <FontAwesomeIcon className="whiteIcon" icon="plus" />
                           </Button>
                        </Col>
                     </Row>
                  );
               })}
            </Col>
         </Row>

         <Modal isOpen={emailToDelete !== null} contentClassName={modalClassName}>
            <ModalHeader children="Confirm Email Deletion" />
            <ModalBody
               children={`Are you sure you want to delete ${emailToDelete}?`}
            />
            <ModalFooter>
               <ButtonGroup>
                  <Button
                     className="button-success d-inline"
                     children="Confirm"
                     onClick={deleteEmail}
                     disabled={locked}
                  />
                  <Button
                     className="button-danger"
                     children="Cancel"
                     onClick={() => setEmailToDelete(null)}
                     disabled={locked}
                  />
               </ButtonGroup>
            </ModalFooter>
         </Modal>
      </>
   );
};

export default function OrderPlacement(props) {
   const userCtx = useContext(UserContext);
   const navigate = useNavigate();
   // The delivery toggles are shown to everyone but only IsAPUCS may change them (SaveInvoiceOrderFlags), exactly as
   // on the dashboard (CustomerInformation) - customers see their preferences without being offered a save that fails.
   const isAPUCS = !!userCtx.currentUser?.isAPUCS;

   const [addresses, setAddresses] = useState([]);
   const [confirmCredit, setConfirmCredit] = useState(false);
   const [confirmDup, setConfirmDup] = useState(false);   // Duplicate PO?
   const [confirmLate, setConfirmLate] = useState(false);
   const [countryList, setCountryList] = useState([]);
   const [customerDetails, setCustomerDetails] = useState(null);
   const [dupCleared, setDupClear] = useState(false);
   const [enterDate, setEnterDate] = useState();
   const [flags, setFlags] = useState({});
   const [formErrors, setFormErrors] = useState(null);
   const [freightCharges, setFreightCharges] = useState({ value: "" });
   const [initialized, setInitialized] = useState(false);
   const [invoiceEmailList, setInvoiceEmailList] = useState();
   const [lateCleared, setLateClear] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [orderContact, setOrderContact] = useState();
   const [orderEmailList, setOrderEmailList] = useState();
   const [orderFax, setOrderFax] = useState();
   const [orderPhone, setOrderPhone] = useState();
   const [orders, setOrders] = useState([]);
   const [reuseOrderDetails, setReuseOrderDetails] = useState(true);
   const [salespeopleList, setSalespeopleList] = useState([]);
   const [salesperson, setSalesperson] = useState();
   const [shippingMethod, setShippingMethod] = useState({});
   const [shippingOptions, setShippingOptions] = useState([]);
   // True from the first Place Order click until the request finishes (or the click path stops short of it),
   // so the buttons are disabled across the duplicate-PO check and the dialogs as well as the POST.
   const [submittingOrder, setSubmittingOrder] = useState(false);
   // The POST itself, as a ref: a state flag is not visible to a second call in the same render, and two
   // callers can reach handleSubmit (the credit dialog's Continue, the duplicate-PO follow-on).
   const postInFlight = useRef(false);
   // Set when Order/PlaceOrder returns a kept cart (something was refused): the header entries typed before
   // the submit, consumed once by the cart-rebuild effect so they survive instead of resetting to defaults.
   const keptHeaders = useRef(null);
   // A Place Order or Create Quote click path (duplicate-PO check, dialogs, POST), as a ref so a second click is
   // refused even before the disabled state has rendered. Set by startClick, cleared by stopSubmitting.
   const clickInFlight = useRef(false);
   // The cart and customer objects the order groups were last built from (see the cart-rebuild effect).
   const lastBuiltCart = useRef(null);
   const lastBuiltCustomer = useRef(null);
   const FreightCharges = [
      { label: "Prepaid", value: "PREPAID" },
      { label: "Collect", value: "COLLECT" },
      { label: "Prepaid & Add", value: "PREPAID & ADD" },
      { label: "3rd Party Billing", value: "3RD PARTY" },
   ];

   useEffect(() => {
      if (dupCleared === true) {
         setConfirmDup(false);
         validateBalances();
         setDupClear(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [dupCleared]);

   useEffect(() => {
      if (lateCleared === true) {
         setConfirmLate(false);
         setLateClear(false);
      }
      else {
         validateCredit();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [lateCleared]);

   useEffect(() => {
      if (!initialized && userCtx) {
         const cn = userCtx.currentUser?.isAnyCustomerTypeRole
            ? helpers.activeCustomerId(userCtx)
            : userCtx?.customerFilter?.value;

         if (_.isNil(cn) && !userCtx.currentUser?.isAnyCustomerTypeRole) {
            toast.error(RESELECT_CUSTOMER_MESSAGE)
            return;
         }

         api
            .fetch(`Order/LoadOrderPage`, {CustomerId: cn,})
            .then((response) => {
               if (response.data.success) {
                  //console.log(response.data);
                  setSalespeopleList(
                     _.chain(response.data.salespeople)
                        .map((sp) => {
                           return { value: sp.salespersonId, label: sp.salespersonText,};
                        })
                        .sortBy((x) => { return x.label; })
                        .value()
                  );
                  setShippingOptions(
                     _.chain(response.data.shippingCompanies)
                        .map((cmp) => {
                           return { value: cmp.ddlValue, label: cmp.ddlDescription };
                        })
                        .sortBy((x) => { return x.label; })
                        .value()
                  );
                  setCountryList(
                     _.chain(response.data.countries)
                        .map((cntry) => {
                           return { value: cntry.id, label: cntry.text };
                        })
                        .sortBy((x) => { return x.label; })
                        .value()
                  );
                  setCustomerDetails(response.data.customerDetails);
                  setAddresses(response.data.addresses);
                  setFlags(response.data.flags);
               }
            })
            .catch(api.catchHandler)
            .finally(() => {
               setInitialized(true);
            });
      }

      // Requires a loaded cart - it builds the order from shoppingCart.cartItems below. The cart is not
      // loaded on a cold page load (only on login / impersonation / via the dashboard), and the account
      // picker's reload can land here cold, so guard rather than crash on a null cart.
      // App.js builds a new UserContext value on every App render, so this effect also fires on unrelated
      // re-renders (opening the cart menu, say), and rebuilding then would reset everything the user entered.
      // Build only for a cart or customer OBJECT not built from yet - identity, not shoppingCartId, so an edited
      // cart (same id, new object) still rebuilds - or when a kept cart's headers are waiting to be restored.
      const cart = userCtx.shoppingCart;
      const inputsChanged = cart !== lastBuiltCart.current || customerDetails !== lastBuiltCustomer.current;
      if (initialized && customerDetails && cart && (inputsChanged || keptHeaders.current)) {
         lastBuiltCart.current = cart;
         lastBuiltCustomer.current = customerDetails;
         // A kept cart (something was refused) rebuilds only the order groups from what is left in it; the
         // order-level fields and each remaining group's header keep what the user entered.
         const kept = keptHeaders.current;
         keptHeaders.current = null;
         const currDate = new Date();
         if (!kept) {
            setEnterDate(dateHelpers.toLongDateString(currDate));
         }
         const followUpDate = dateHelpers.formatDateForServer(
            new Date(currDate.setDate(currDate.getDate() + 45))
         );

         if (!kept) {
            if (userCtx.currentUser?.isSalesperson) {
               setSalesperson(salespeopleList[0].salespersonValue);
            } else {
               // GP holds the salesperson ID twice - RM00101 on the customer, RM00301 in the master this
               // dropdown is built from - and the two can differ in CASE (prod had CUSTSERV vs CustServ).
               // SQL's collation hides that mismatch; === does not, and an unmatched find left the
               // dropdown blank and crashed submit. _.toUpper is null-safe on a missing ID.
               const salespersonId = _.toUpper(customerDetails.salespersonId);
               setSalesperson(
                  _.find(salespeopleList, (sp) => _.toUpper(sp.value) === salespersonId)
               );
            }
            setOrderPhone(customerDetails.phone);
            setOrderFax(customerDetails.fax);
            setOrderContact(customerDetails.contact);

            setShippingMethod(
               _.find(shippingOptions, (opt) => {
                  return opt.label === customerDetails.defaultShippingMethod;
               })
            );
            setFreightCharges(customerDetails.freightCharges
               ? _.find(FreightCharges, (frt) => {
                  if (!frt) return false;
                  return frt.value === customerDetails.freightCharges;
               })
               : { value: "" }
            );

            const billAddress = _.find(addresses, (addr) => {
               return addr.addressCode === customerDetails.primaryBillToCode;
            });
            setOrderEmailList([userCtx.currentUser.email]);
            setInvoiceEmailList(billAddress.emailList);
         }

         const splitOrders = {};
         const orderDefaults = {
            billToCode: customerDetails.primaryBillToCode,
            shipToCode: customerDetails.primaryShipToCode,
            poNumber: "",
            orderNote: "",
            followUpNote: "",
            followUpDate: followUpDate,
            billToAddress: _.find(
               addresses,
               (address) => address.addressCode === customerDetails.primaryBillToCode
            ),
            shipToAddress: _.find(
               addresses,
               (address) => address.addressCode === customerDetails.primaryShipToCode
            ),
            newAddress: {
               addressCode: "-- New Address --",
               country: "",
               address1: "",
               address2: "",
               address3: "",
               city: "",
               state: "",
               zip: "",
               contact: "",
               phone: "",
               fax: "",
            },
         };

         _.forEach(cart.cartItems, (item) => {
            if (item.itemClass === "MACH1") {
               if (splitOrders[item.orderSite]) {
                  splitOrders[item.orderSite].items.push(item);
               } else {
                  splitOrders[item.orderSite] = _.assign({}, orderDefaults, {
                     items: [item],
                     siteName: item.orderSite,
                  });
               }
            } else if (item.itemNumber.startsWith("SP0")) {
               if (splitOrders[`${item.orderSite}-Sample`]) {
                  splitOrders[`${item.orderSite}-Sample`].items.push(item);
               } else {
                  splitOrders[`${item.orderSite}-Sample`] = _.assign(
                     {},
                     orderDefaults,
                     { items: [item], siteName: `${item.orderSite}-Sample` }
                  );
               }
            } else {
               if (splitOrders[item.orderSite]) {
                  splitOrders[`${item.orderSite}`].items.push(item);
               } else {
                  splitOrders[`${item.orderSite}`] = _.assign({}, orderDefaults, {
                     items: [item],
                     siteName: item.orderSite,
                  });
               }
            }
         });
         if (kept) {
            _.forEach(splitOrders, (group, key) => {
               // With "reuse order details" on, the user typed into the first group's header for all of them.
               const typed = kept.reuse ? kept.shared : kept.bySite[group.siteName];
               if (typed) {
                  splitOrders[key] = _.assign({}, group, _.omit(typed, ["items", "siteName"]));
               }
            });
         }
         setOrders(_.sortBy(splitOrders, ["siteNumber"]));
         setLoaded(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded, customerDetails, userCtx]);

   function handleFlagChange(flag) {
      var flagsCopy = _.assign({}, flags);
      flagsCopy[flag] = !flagsCopy[flag];
      setFlags(flagsCopy);

      api
         .post(`Customer/SaveInvoiceOrderFlags`, flagsCopy)
         .then((response) => {
            if (response.data.success) {
               toast.success("Email Settings updated");
            } else {
               toast.error("Error saving Email Settings");
            }
         })
         .catch(api.catchHandler);
   }

   // The one re-entry guard for BOTH entry points (Place Order, Create Quote): refused while a click path or a POST
   // is already running, so no second submit or stray dialog can start. Released by stopSubmitting.
   function startClick() {
      if (clickInFlight.current || postInFlight.current) return false;
      clickInFlight.current = true;
      setSubmittingOrder(true);
      return true;
   }

   // Every way a click path ends - the POST finishing, or any stop short of it.
   function stopSubmitting() {
      clickInFlight.current = false;
      setSubmittingOrder(false);
   }

   function handleNegativeConfirm() {
      stopSubmitting();
      setConfirmCredit(false);
      setConfirmDup(false);
      setConfirmLate(false);
      navigate("/customer-dashboard");
   }

   function handleNewAddress(siteNum, field, value) {
      const ordersCopy = _.slice(orders);
      ordersCopy[siteNum].shipToAddress[field] = value;
      setOrders(ordersCopy);
   }

   function handleSubmit(isQuote) {
      if (postInFlight.current) return;
      setFormErrors([]);
      if (!validateOrderForm(isQuote)) {
         stopSubmitting();
         return;
      }

      const payload = {
         CustomerId: userCtx.shoppingCart.customerId,
         EnterDate: dateHelpers.formatDateForServer(enterDate),
         FreightCharges: freightCharges.value,
         InvoiceCCEmails: _.join(invoiceEmailList, ";"),
         IsQuote: isQuote,
         LastUpdatedByUserId: userCtx.currentUser.id,
         OrderCCEmails: _.join(orderEmailList, ";"),
         OrderContact: orderContact,
         OrderFax: orderFax,
         OrderPhone: orderPhone,
         ReuseOrderDetails: reuseOrderDetails,
         SalespersonId: salesperson.value,
         ShipMethod: shippingMethod.value,
         ShoppingCartId: userCtx.shoppingCart.shoppingCartId,
         OrderHeaders: reuseOrderDetails
            ? _.fill(Array(orders.length), orders[0])
            : orders,
      };

      var submitText = (isQuote === true) ? "Submitting Quote. Please wait" : "Submitting Order. Please wait";
      const submittingToast = toast.info(submitText);
      postInFlight.current = true;
      setSubmittingOrder(true);
      api
         .post(`Order/PlaceOrder`, payload)
         .then((response) => {
            if (response.data.success) {
               // success means the call ran; each order group reports its own outcome and sentence. A failed
               // cart refresh is folded into the same toast as one more line.
               showPlacementResults(response.data.results ?? [],
                  response.data.cartRefreshFailed ? [CART_REFRESH_FAILED_MESSAGE] : []);
               if (response.data.cartRefreshFailed) {
                  // The results are real - only the cart reload failed. Drop the stale cart rather than let it
                  // be placed again, and send the user somewhere that does not build an order from it.
                  userCtx.setShoppingCart(null);
                  navigate("/customer-dashboard");
               } else if (response.data.cart) {
                  // Something was refused or not placed: the same cart comes back holding only those groups' lines,
                  // so stay here to fix the cause and place again - with the headers the user typed.
                  keptHeaders.current = {
                     reuse: reuseOrderDetails,
                     shared: orders[0],
                     bySite: _.keyBy(orders, "siteName"),
                  };
                  userCtx.setShoppingCart(response.data.cart);
               } else {
                  userCtx.setShoppingCart(response.data.emptyCart);
                  navigate("/customer-dashboard");
               }
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            // The app's ToastContainer shows one toast at a time, so the results queue behind this one.
            toast.dismiss(submittingToast);
            postInFlight.current = false;
            stopSubmitting();
         });
   }

   function updateOrderDetails(siteNum, field, value) {
      const ordersCopy = _.slice(orders);
      ordersCopy[siteNum][field] = value;
      if (field === "billToCode") {
         ordersCopy[siteNum].billToAddress = _.find(addresses, (address) => address.addressCode === value);
      }
      if (field === "shipToCode") {
         if (value !== "-- New Address --") {
            ordersCopy[siteNum].shipToAddress = _.find(addresses, (address) => address.addressCode === value);
         } else {
            ordersCopy[siteNum].shipToAddress = ordersCopy[siteNum].newAddress;
         }
      }
      setOrders(ordersCopy);
   }

   function validateBalances() {
      // If no late balance and no credit limit just do regular process
      if (customerDetails.lateBalance <= 0 && customerDetails.creditLimit <= 0) {
         handleSubmit(false);
         return;
      }

      // Validate user wants (and can) continue if a late balance, else move on to validate credit limit
      if (customerDetails.lateBalance > 0) {
         if (userCtx.currentUser.isAnyCustomerTypeRole) {
            // Customers cannot place an order with this condition in place. Hard stop here - return so we
            // never fall through to the internal-user "continue anyway" dialog below.
            toast.error("You have late unpaid invoices and must make a payment to continue.");
            stopSubmitting();
            navigate("/customer-dashboard");
            return;
         }
         setConfirmLate(true);
         return;
      }
      else {
         validateCredit();
      }
   }

   function validateCredit() {
      if (customerDetails) {
         if (customerDetails.creditLimit > 0) {
            // Null-safe: the cart is not loaded on a cold page load (only on login / via the dashboard),
            // and the picker's reload can land here cold. The 0 seed also fixes an empty-cart reduce,
            // which threw "Reduce of empty array with no initial value".
            var extPrice = (userCtx.shoppingCart?.cartItems ?? []).map(i => i.extendedPrice).reduce((a, b) => a + b, 0);
            if ((customerDetails.customerBalance + extPrice) > customerDetails.creditLimit) {
               if (userCtx.currentUser.isAnyCustomerTypeRole) {
                  // Customers cannot place an order with this condition in place. Hard stop here - return so we
                  // never fall through to the internal-user "continue anyway" dialog below.
                  toast.error("This order will exceed your credit limit and cannot be created at this time.");
                  stopSubmitting();
                  navigate("/customer-dashboard");
                  return;
               }
               setConfirmCredit(true)
            }
            else { handleSubmit(false); }
         }
         else { handleSubmit(false); }
      }
      else {
         // Also reached from the lateCleared effect on first render, where nothing is in flight.
         stopSubmitting();
      }
   }

   function validateDuplicatePO() {
      // In flight from the click: the duplicate-PO check is a server call, and a second click during it must
      // not start a second submit. Every path that stops short of the POST clears the flag again.
      if (!startClick()) return;
      if (userCtx.currentUser.isAnyCustomerTypeRole) {
         validateBalances(); // Customers will not need this check.
         return;
      }
      const custId = userCtx?.customerFilter?.value || "";
      if (custId === "") {
         // The duplicate check needs the selected customer; say so rather than silently doing nothing.
         toast.error(RESELECT_CUSTOMER_MESSAGE);
         stopSubmitting();
         return;
      }
      const poChecks = [];
      _.forEach(orders, (order, index) => {
         if (reuseOrderDetails && index > 0) return;
         if (order?.poNumber && order?.poNumber.length) {
            poChecks.push(api.post(`Order/IsDuplicatePurchaseOrder/${custId}/${order.poNumber}`));
         }
      });
      if (poChecks.length === 0) {
         // No PO number to check. This click used to end silently; now it takes the normal path. The form check
         // runs first so the "missing a PO number" errors show before any balance or credit dialog - handleSubmit
         // runs it again, so nothing is skipped either way.
         if (!validateOrderForm(false)) {
            stopSubmitting();
            return;
         }
         validateBalances();
         return;
      }
      // One decision over ALL the checks. Deciding per reply let two "not a duplicate" replies start two
      // submits, and let one group's "not a duplicate" skip the confirm another group's duplicate needed.
      // allSettled, so a second failing check is never an unhandled rejection.
      Promise.allSettled(poChecks)
         .then((settled) => {
            const broken = _.find(settled, (outcome) => outcome.status === "rejected" || !isWellFormedReply(outcome.value));
            if (broken) {
               api.catchHandler(broken.status === "rejected" ? broken.reason : broken.value);
               toast.error(PO_CHECK_FAILED_MESSAGE);
               stopSubmitting();
               return;
            }
            const responses = _.map(settled, "value");
            const failed = _.find(responses, (response) => !response.data.success);
            if (failed) {
               toast.error(failed.data.message || PO_CHECK_FAILED_MESSAGE);
               stopSubmitting();
            } else if (_.some(responses, (response) => response.data.isDup === true)) {
               setConfirmDup(true);
            } else {
               setDupClear(true);
            }
         })
         .catch((error) => {
            // Last resort: anything thrown above must never leave the buttons locked until a reload.
            api.catchHandler(error);
            toast.error(PO_CHECK_FAILED_MESSAGE);
            stopSubmitting();
         });
   }

   function validateOrderForm(isQuote) {
      const errorList = [];

      setFormErrors([]);
      if (!freightCharges || !freightCharges.label) {
         errorList.push("Please specify frieght charges");
      }
      if (!shippingMethod || !shippingMethod.value) {
         errorList.push("Please specify a shipping method");
      }
      // handleSubmit dereferences salesperson.value directly, and nothing else guarantees it is set:
      // the customer's GP salesperson may not match the dropdown, and the Select is isClearable so the
      // user can blank it. Without this guard the payload build throws and the app-wide ErrorBoundary
      // replaces the entire screen with "Something went wrong on our side."
      if (!salesperson || !salesperson.value) {
         errorList.push("Please specify a salesperson");
      }
      _.forEach(orders, (order, index) => {
         if (reuseOrderDetails && index > 0) {
            return;
         }

         if (!order.poNumber || !order.poNumber.length) {
            if (isQuote === true) {
               // Quotes are allowed a blank PO
            }
            else if (reuseOrderDetails) {
               errorList.push(`Order is missing a PO number!`);
            } else {
               errorList.push(`Order from site ${order.siteName} is missing a PO number!`);
            }
         }
         if (!order.billToCode || !order.shipToCode) {
            if (reuseOrderDetails) {
               errorList.push(`Order is missing address information!`);
            } else {
               errorList.push(`Order from site ${order.siteName} is missing address information`);
            }
         }
         if (order.shipToCode === "-- New Address --") {
            if (!order.shipToAddress.country)
               errorList.push("Please select a Country");
            if (!order.shipToAddress.address1)
               errorList.push("Please provide a Company Name");
            if (!order.shipToAddress.address2)
               errorList.push("Please provide an Address");
            if (!order.shipToAddress.city) errorList.push("Please provide a City");
            if (!order.shipToAddress.state)
               errorList.push("Please provide a State");
            if (!order.shipToAddress.zip)
               errorList.push("Please provide a Zip Code");
         }

         if (order.siteName === "PLT") {
            _.forEach(order.items, (item) => {
               if (_.startsWith(item.itemClass, "MACH")) {
                  errorList.push(
                     `Machine Parts can NOT come from Site PLT. Please reassign or remove item ${item.itemNumber} to proceed`
                  );
               }
            });
         }

         if (order.siteName === "FLEX05") {
            _.forEach(order.items, (item) => {
               if (_.startsWith(item.itemNumber, "SP0")) {
                  errorList.push(
                     `Sample Items can NOT come from Site ${order.siteName}. Please reassign or remove item ${item.itemNumber} to proceed`
                  );
               }
               else if (_.startsWith(item.itemClass, "MACH")) {
                  errorList.push(
                     `Machine Parts can NOT come from Site ${order.siteName}. Please reassign or remove item ${item.itemNumber} to proceed`
                  );
               }
            });
         }

         if (
            !_.find(constants.VALID_ORDER_SITES, (validSite) => {
               return validSite === _.trim(order.siteName, "-Sample");
            })
         ) {
            errorList.push(
               `Currently, the only acceptable order sites are PLT, PLTWHSE, or FLEX05. Please reassign or remove the items from ${order.siteName} to proceed`
            );
         }
      });
      if (errorList.length) {
         setFormErrors(
            _.reject(_.uniq(_.concat(errorList, formErrors)), (x) => !x)
         );
         return false;
      }
      setFormErrors([]);
      return true;
   }

   return (
      <DesktopContainer>
         <ServiceThreadSpinner on={!loaded}>
            <h4 className="orderDetailHeader">Order Details</h4>
            <Card className="dashboard-card">
               <CardBody>
                  <Row>
                     <Col xs={6}>
                        <Row>
                           <Col xs={2}>
                              <div children="Customer:" className="fw-bold" />
                           </Col>
                           <Col xs={10}>
                              <Input
                                 className="readonly-input"
                                 value={loaded && customerDetails.ddlText}
                                 readOnly
                              />
                           </Col>
                        </Row>
                        <Row>
                           <Col xs={2}>
                              <div children="Date:" className="fw-bold" />
                           </Col>
                           <Col xs={10}>
                              <Input
                                 className="readonly-input"
                                 value={enterDate}
                                 readOnly
                              />
                           </Col>
                        </Row>
                        <OrderEmailList
                           emailList={orderEmailList}
                           setEmailList={setOrderEmailList}
                           locked={submittingOrder}
                           quickEmails={_.reduce(
                              addresses,
                              (allEmails, addr) => {
                                 return _.concat(allEmails, addr.emailList);
                              },
                              []
                           )}
                           userCtx={userCtx}
                        />
                     </Col>

                     <Col xs={6}>
                        <Row className="mb-2">
                           <Col xs={2} className="p-0 mt-2">
                              <div children="Salesperson:" className="fw-bold" />
                           </Col>
                           <Col xs={10}>
                              <Select
                                 classNamePrefix="serviceThreadSelect"
                                 filterName="SalesPersonId"
                                 options={salespeopleList}
                                 value={salesperson}
                                 onChange={(e) => setSalesperson(e)}
                                 isClearable
                                 isDisabled={submittingOrder}
                              />
                           </Col>
                        </Row>
                        <Row className="mb-2">
                           <Col xs={2} className="p-0 mt-2">
                              <div children="Phone:" className="fw-bold" />
                           </Col>
                           <Col xs={10}>
                              <NumberFormat
                                 id="phone"
                                 className="form-control"
                                 format="(###) ###-####"
                                 mask="_"
                                 maxLength="15"
                                 value={orderPhone || ""}
                                 disabled={submittingOrder}
                              />
                           </Col>
                        </Row>
                        <Row className="mb-2">
                           <Col xs={2} className="p-0 mt-2">
                              <div children="Fax:" className="fw-bold" />
                           </Col>
                           <Col xs={10}>
                              <NumberFormat
                                 id="fax"
                                 className="form-control"
                                 format="(###) ###-####"
                                 mask="_"
                                 maxLength="15"
                                 value={orderFax || ""}
                                 disabled={submittingOrder}
                              />
                           </Col>
                        </Row>
                        <Row className="mb-2">
                           <Col xs={2} className="p-0 mt-2">
                              <div children="Contact:" className="fw-bold" />
                           </Col>
                           <Col xs={10}>
                              <Input id="mainContact" value={orderContact || ""} disabled={submittingOrder} />
                           </Col>
                        </Row>
                        <Row className="mb-2">
                           <Col xs={2} className="p-0 mt-2">
                              <div children="Freight Charges:" className="fw-bold" />
                           </Col>
                           <Col xs={10}>
                              <Select
                                 classNamePrefix="serviceThreadSelect"
                                 options={FreightCharges}
                                 value={_.find(FreightCharges, (opt) => {
                                    if (!opt || !freightCharges) return false;
                                    return opt.value === freightCharges.value;
                                 })}
                                 onChange={(e) => setFreightCharges(e)}
                                 isDisabled={submittingOrder}
                              />
                           </Col>
                        </Row>
                        <Row>
                           <Col xs={2} className="p-0 mt-2">
                              <div children="Ship Method:" className="fw-bold" />
                           </Col>
                           <Col xs={10}>
                              <Select
                                 classNamePrefix="serviceThreadSelect"
                                 options={shippingOptions}
                                 value={_.find(shippingOptions, (opt) => opt === shippingMethod)}
                                 onChange={(e) => setShippingMethod(e)}
                                 isSearchable
                                 isDisabled={submittingOrder}
                              />
                           </Col>
                        </Row>
                        <Row className="mt-2">
                           <Col xs={3} className="p-0">
                              <div children="Confirmation Option(s):" className="fw-bold" />
                           </Col>
                           <Col xs={2}>
                              <Input
                                 type="checkbox"
                                 className="ms-3"
                                 checked={flags.emailOrder}
                                 onChange={() => handleFlagChange("emailOrder")}
                                 disabled={!isAPUCS}
                              />
                              <Label className="ms-2">Email</Label>
                           </Col>
                           <Col xs={2}>
                              <Input
                                 type="checkbox"
                                 checked={flags.faxOrder}
                                 onChange={() => handleFlagChange("faxOrder")}
                                 disabled={!isAPUCS}
                              />
                              <Label className="ms-2">Fax</Label>
                           </Col>
                        </Row>
                     </Col>
                  </Row>
               </CardBody>
            </Card>

            <h4 className="orderDetailHeader">Invoice Details</h4>
            <Card className="dashboard-card">
               <CardBody>
                  <Row>
                     <Col>
                        <OrderEmailList
                           emailList={invoiceEmailList}
                           setEmailList={setInvoiceEmailList}
                           locked={submittingOrder}
                           quickEmails={_.reduce(
                              addresses,
                              (allEmails, addr) => {
                                 return _.concat(allEmails, addr.emailList);
                              },
                              []
                           )}
                           userCtx={userCtx}
                        />
                     </Col>

                     <Col>
                        <Row className="orderDetailForm">
                           <Col xs={2}>
                              <div children="Invoice Option(s):" className="fw-bold" />
                           </Col>
                           <Col xs={2}>
                              <Input
                                 type="checkbox"
                                 className="ms-3"
                                 checked={flags.emailInvoice}
                                 onChange={(e) => handleFlagChange("emailInvoice")}
                                 disabled={!isAPUCS}
                              />
                              <Label className="ms-2" check>
                                 Email
                              </Label>
                           </Col>
                           <Col xs={2}>
                              <Input
                                 type="checkbox"
                                 checked={flags.faxInvoice}
                                 onChange={(e) => handleFlagChange("faxInvoice")}
                                 disabled={!isAPUCS}
                              />
                              <Label className="ms-2" check>
                                 Fax
                              </Label>
                           </Col>
                           <Col xs={2}>
                              <Input
                                 type="checkbox"
                                 checked={flags.postInvoice}
                                 onChange={(e) => handleFlagChange("postInvoice")}
                                 disabled={!isAPUCS}
                              />
                              <Label className="ms-2" check>
                                 Postal
                              </Label>
                           </Col>
                        </Row>
                     </Col>
                  </Row>
               </CardBody>
            </Card>

            <div>
               <span className="h4 orderDetailHeader">Orders</span>
               <span>
                  <Input
                     type="checkbox"
                     checked={reuseOrderDetails}
                     className="ms-3 me-2 mt-2"
                     onChange={() => setReuseOrderDetails(!reuseOrderDetails)}
                     disabled={submittingOrder}
                  />
                  <Label children="Use same PO/Address Info for all orders in cart" />
               </span>
            </div>
            {_.map(orders, (order, index) => {
               return (
                  <Card className="dashboard-card" key={index}>
                     <CardBody>
                        <OrderDetails
                           addresses={addresses}
                           countryList={countryList}
                           poNumber={order.poNumber}
                           billToAddressCode={order.billToCode}
                           shipToAddressCode={order.shipToCode}
                           orderNote={order.orderNote}
                           followUpDate={order.followUpDate}
                           followUpNote={order.followUpNote}
                           newAddressInfo={order.newAddress}
                           reuseDetails={reuseOrderDetails}
                           siteNumber={index}
                           siteName={order.siteName}
                           updateOrderDetails={updateOrderDetails}
                           updateOrderShipTo={handleNewAddress}
                           locked={submittingOrder}
                        />
                        <CartProductCard
                           key={order.siteName}
                           siteName={order.siteName}
                           cartItems={order.items}
                           allowInlineEditsDeletes={false}
                        />
                        <br />
                     </CardBody>
                  </Card>
               );
            })}
            {formErrors ? (
               <Row className="mt-3">
                  <Col>
                     {_.map(formErrors, (error, index) => (
                        <ReactstrapAlert
                           key={`${_.camelCase(error)}${index}`}
                           color="danger"
                           children={error}
                        />
                     ))}
                  </Col>
               </Row>
            ) : null}
            <Row>
               <Col>
                  <ButtonGroup className="float-end">
                     <Button
                        children="Place Order"
                        className="shredButton float-end"
                        onClick={() => validateDuplicatePO()} // validateBalances()
                        disabled={submittingOrder}
                     />
                     {userCtx.currentUser.isQuoteRole && (
                        <Button
                           children="CREATE QUOTE"
                           className="shredButtonGray float-end"
                           onClick={() => startClick() && handleSubmit(true)}
                           disabled={submittingOrder}
                        />
                     )}
                     <Button
                        children="Cancel"
                        onClick={() => navigate(-1)}
                        disabled={submittingOrder}
                     />
                  </ButtonGroup>
               </Col>
            </Row>

            <ConfirmModal
               confirmDelete={confirmLate}
               approveConfirmDelete={() => setLateClear(true)}
               declineConfirmDelete={() => handleNegativeConfirm()}
               header={`Please Confirm`}
               yesText={`Continue`}
               noText={`Cancel`}
               warningMessage={
                  <>
                     Customer has late unpaid invoices. Please contact for payment before completing the order.
                     <br /><br />
                     Select continue to make the order anyway.
                  </>
               }
            />

            <ConfirmModal
               confirmDelete={confirmCredit}
               approveConfirmDelete={() => {
                  setConfirmCredit(false);
                  handleSubmit(false);
               }}
               declineConfirmDelete={() => handleNegativeConfirm()}
               header={`Please Confirm`}
               yesText={`Continue`}
               noText={`Cancel`}
               warningMessage={
                  <>
                     This order will exceed the customer's credit limit. Please contact the account manager for approval.
                     <br /><br />
                     <strong>
                        Great Plains may refuse this order until the credit limit is raised.
                     </strong>{" "}
                     If it does, nothing is saved and the order stays in the cart, so you can place it again once
                     the limit is raised.
                     <br /><br />
                     Select Continue to try anyway.
                  </>
               }
            />

            <ConfirmModal
               confirmDelete={confirmDup}
               approveConfirmDelete={() => setDupClear(true)}
               declineConfirmDelete={() => handleNegativeConfirm()}
               header={`Please Confirm`}
               yesText={`Continue`}
               noText={`Cancel`}
               warningMessage={
                  <>
                     Duplicate PO exists. Contact customer to verify.
                     <br /><br />
                     Select continue to make the order anyway.
                  </>
               }
            />

         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
